import { Component, OnDestroy, OnInit } from '@angular/core';
import { HomeworkFormComponent as AmHomeworkFormComponent, Configuration } from '@lemberger/lemberger-am';
import { SearchResult } from '../../../../../models/search-result';
import { HomeworkLink } from '../../../../../models/homework-link';
import { HomeworkService } from '../../../../../services/homework.service';
import { QuickLinkService } from '../../../../../services/quick-link.service';
import { PageService } from '../../../../../services/page.service';
import { environment } from '../../../../../../environments/environment';
import { ApplicationStateService } from '../../../../../services/application-state.service';
import { CommunicationService } from '../../../../../services/communication.service';
import { AssetService } from '../../../../../services/asset.service';
import { Subscription } from 'rxjs';
import { ModalService } from '../../../../../services/modal.service';
import { HomeworkFactory } from '../../../../../models/factories/homework-factory';
import _ from 'lodash';
import { RestService } from '../../../../../services/rest.service';
import { RouteService } from '../../../../../services/route.service';
import { QuickLinkSearchResult } from '../../../../../models/quick-link-search-result';
import { SchoolClassService } from '../../../../../services/school-class.service';
import { AlertService } from '../../../../../services/alert.service';
import { SubAssignment } from '../../../../../models/sub-assignment';
import { TranslationPipe } from '../../../../../pipes/translation.pipe';
import { AssetPipe } from '../../../../../pipes/asset.pipe';
import { FormsModule } from '@angular/forms';

@Component({
    selector: 'homework-form',
    templateUrl: './homework-form.component.html',
    styleUrls: ['./homework-form.component.scss'],
    standalone: true,
    imports: [
        TranslationPipe,
        AssetPipe,
        FormsModule,
        AmHomeworkFormComponent
    ]
})
export class HomeworkFormComponent implements OnInit, OnDestroy {

    title: string;
    email: string;
    secondEmail: string;
    prefix: string;
    cooldownPhase: number | null = null;

    hsmConfig: Configuration;

    homeworkAddedSubject: Subscription;
    homeworkChangeSubject: Subscription;
    addHomeworkSubscription: Subscription;

    errorMessages = [];
    loading = false;
    loadingRecommendations = false;

    selectedBook: { id: number; title: string; coverPath: string; prefix?: string };
    bookData: Array<{ id: number; title: string; coverPath: string; prefix?: string }>;

    recommendations = [];
    private debounceUpdateRecommendations = _.debounce(this.updateRecommendations, 300);

    constructor(public applicationStateService: ApplicationStateService,
                public assetService: AssetService,
                public homeworkService: HomeworkService,
                private communicationService: CommunicationService,
                private quickLinkService: QuickLinkService,
                private alertService: AlertService,
                private pageService: PageService,
                private modalService: ModalService,
                private restService: RestService,
                private routeService: RouteService,
                public schoolClassService: SchoolClassService) {

        this.homeworkChangeSubject = this.communicationService.homeworkChangeAnnounced$.subscribe(() => {
            this.fillHomework(this.homeworkService.getActualHomework());
            this.debounceUpdateRecommendations();
        });

        this.homeworkAddedSubject = this.communicationService.addHomeworkAnnounced$.subscribe(() => {
            this.debounceUpdateRecommendations();
            this.fillHomework(this.homeworkService.getActualHomework());
        });
    }

    ngOnInit(): void {
        this.schoolClassService.init();
        this.hsmConfig = {
            user: this.applicationStateService.userData as any,
            bookConfig: this.applicationStateService.bookConfig as any,
            embedded: true
        };
        this.prefix = this.applicationStateService.bookConfig.quickLinkPrefix?.toLowerCase();
        this.homeworkService.formAddedHomeworks = [];

        this.fillHomework(this.homeworkService.getActualHomework());
        this.updateRecommendations();

        const route = this.routeService.getRelativeRoute(environment.endpointUrls.quickLinkBookSearch, {});
        this.bookData = [
            {
                id: -1,
                coverPath: undefined,
                title: 'Beispiele aus <strong>allen Büchern</strong>',
                prefix: undefined
            },
            {
                id: -2,
                coverPath: this.pageService.getPageByIndex(0).thumbnailImage.url,
                title: 'Beispiele aus <strong>diesem Buch</strong>',
                prefix: this.applicationStateService.bookConfig.quickLinkPrefix
            }
        ];

        this.selectedBook = this.bookData[1];

        this.restService.get(route).then(result => {

            this.bookData = [
                {
                    id: -1,
                    coverPath: undefined,
                    title: 'Beispiele aus <strong>allen Büchern</strong>',
                    prefix: undefined
                },
                {
                    id: -2,
                    coverPath: this.pageService.getPageByIndex(0).thumbnailImage.url,
                    title: 'Beispiele aus <strong>diesem Buch</strong>',
                    prefix: this.applicationStateService.bookConfig.quickLinkPrefix
                },
                ...result.filter(publicationWrapper =>
                    publicationWrapper.prefix !== this.applicationStateService.bookConfig.quickLinkPrefix)
            ];
            this.selectedBook = this.bookData[1];
        });

        if (this.applicationStateService.bookConfig.quickLinkPrefix) {
            this.prefix = this.applicationStateService.bookConfig.quickLinkPrefix.toLowerCase();
        }

        if (this.editMode()) {
            this.homeworkService.activateHomework();
        }

        this.initFields();

        this.addHomeworkSubscription = this.homeworkService.addHomeworkExercise$.subscribe((value: any) => {
            this.addResult(value.result, false, value.teaser, value.preview);
        });
    }

    ngOnDestroy(): void {
        this.communicationService.clearHomework();
        this.loading = false;
        this.homeworkAddedSubject.unsubscribe();
        this.homeworkChangeSubject.unsubscribe();
        this.addHomeworkSubscription.unsubscribe();
    }

    /**
     * @param {SearchResult} result
     * @param recommended: If result is recommended.
     * @param teaser
     */
    addResult(result: string, recommended?: boolean, teaser?: string, preview?: string): void {
        const prefix = !recommended ? this.selectedBook.prefix : undefined;

        this.quickLinkService.findQuickLinks(result, prefix, false).then(value => {
            // Check for related Exercises
            const quicklinkObject = value[0].quicklinksExtended.find(element => element.prefix + element.slug === result);

            if (quicklinkObject && quicklinkObject.related) {
                let recommendations = [];

                if (typeof quicklinkObject.related === 'string') {
                    recommendations = quicklinkObject.related.split(',');

                } else if (Array.isArray(quicklinkObject.related)) {
                    recommendations = quicklinkObject.related;

                } else {
                    console.error('Invalid type of quicklink related exercises');
                }

                recommendations.forEach(element => {
                    if (_.filter(this.homeworkService.formAddedHomeworks, ele => {
                        return ele.text === element;
                    }).length === 0) {
                        this.addResult(element);
                    }
                });
            }

            // Lowercase because it should should work for e.g. 'GM1B1' and 'gm1b1'
            const quicklinks = this.getQuickLinksFromSearchQuery(value);

            // Only add quicklink to homework if the quicklink has been found
            if (quicklinks && quicklinks.includes(result.toLowerCase())) {
                this.clearErrors();
                if (_.filter(this.homeworkService.formAddedHomeworks, ele => {
                    return ele.text === result;
                }).length > 0) {
                    this.errorMessages.push('Diese Aufgabe wurde bereits hinzugefügt.');

                    return;
                }

                const searchResult = new SearchResult();
                searchResult.text = result;
                searchResult.teaser = teaser;
                searchResult.preview = preview;

                this.quickLinkService.fetchAssignmentData(result).then((assignmentData) => {
                    // only add sub-assignments if assignmentData as more than one assignment
                    if (assignmentData?.body && assignmentData.body.assignments && assignmentData.body.assignments.length > 1) {
                        for (const assignment of assignmentData.body.assignments.entries()) {
                            const subAssignment = new SubAssignment();
                            subAssignment.index = assignment[0];
                            subAssignment.nr = ('nr' in assignment[1] && typeof assignment[1].nr === 'string') ? assignment[1].nr : '';

                            searchResult.subAssignments.push(subAssignment);
                        }
                    }

                    this.homeworkService.formAddedHomeworks.push(searchResult);
                    this.syncHomework();

                    this.communicationService.announceHomeworkChange();
                }).catch(() => {
                    this.homeworkService.formAddedHomeworks.push(searchResult);
                    this.syncHomework();

                    this.communicationService.announceHomeworkChange();
                });
            } else {
                this.errorMessages.push(`Die Aufgabe "${result}" konnte leider nicht gefunden werden.`);
            }
        }).catch(err => {
            this.errorMessages.push('Ein unbekannter Fehler ist aufgetreten.');
            console.error(err);
        });
    }

    /**
     * fill form inputs with given homework infos
     *
     * @param {HomeworkLink} hw
     */
    fillHomework(hw: HomeworkLink): void {
        this.homeworkService.formAddedHomeworks = hw.exercises;
    }

    /**
     * is homework edit mode on
     *
     * @returns {boolean}
     */
    editMode(): boolean {
        return this.homeworkService.isHomeworkEditorOpened();
    }

    async toggle(): Promise<void> {
        if (this.homeworkService.formAddedHomeworks.length && this.editMode()) {
            if (!await this.alertService.alert(AlertService.HOMEWORK_CONFIRMATION)) {
                return;
            }
        }
        this.homeworkService.formAddedHomeworks = [];
        this.syncHomework();
        this.communicationService.announceHomeworkChange();
        this.homeworkService.triggerHomeworkEditor();
    }

    isValidEmail(email): boolean {
        // eslint-disable-next-line max-len
        const regex = /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;

        return regex.test(email);
    }

    clearErrors(): void {
        this.errorMessages = [];
    }

    getQuickLinksFromSearchQuery(queryResult: Array<QuickLinkSearchResult>): Array<string> {
        return _.flattenDeep(queryResult.map(ele => queryResult.map(ele => ele.quicklinksExtended
            .map(ql => (ele.bookWrapper.prefix + ql.slug).toLowerCase()))));
    }

    updateRecommendations(): void {
        const recommendationsFor = this.homeworkService.formAddedHomeworks.map(item => {
            return this.homeworkService.convertExercise(item);
        });

        if (recommendationsFor.length === 0) {
            this.recommendations = [];

            return;
        }

        this.loadingRecommendations = true;
        this.homeworkService.fetchRecommendations(recommendationsFor || [])
            .then(response => {
                this.loadingRecommendations = false;
                this.recommendations = response;
            }).catch(err => {
            this.loadingRecommendations = false;
        });
    }

    /**
     * store actual added exercises globally in homework service
     */
    private syncHomework(): void {
        const homework = new HomeworkLink();
        homework.exercises = this.homeworkService.formAddedHomeworks;
        homework.title = this.title;
        homework.email = this.email;
        homework.secondEmail = this.secondEmail;
        homework.cooldownPhase = this.cooldownPhase;
        this.homeworkService.syncHomework(homework);
    }

    private initFields(): void {
        if (!this.email) {
            if (this.isValidEmail(this.applicationStateService.userData.email)) {
                this.email = this.applicationStateService.userData.email;
            }
        }

        const today = new Date();
        const options = { year: 'numeric', month: '2-digit', day: '2-digit' } as const;
        const currentDateAsString = today.toLocaleDateString('de-DE', options);

        if (!this.title) {
            this.title = `Aufgabe vom ${currentDateAsString}`;
        }
    }

    onFinishHomework(event: boolean): void {
        this.communicationService.clearHomework();
    }

    onHomeworkCreated(homework: any): void {
        const homeworkFactory = new HomeworkFactory();
        const instantiatedHomework = homeworkFactory.buildFromObject(homework);
        instantiatedHomework.bypassCheck = true;

        if (homework.notifySchoolClass && homework.notifySchoolClass > 0) {
            let notifyText = `${homework.notifySchoolClassText || ''} - `;
            if (homework.notifySchoolClassText === undefined ||
                homework.notifySchoolClassText === '' ||
                homework.notifySchoolClassText === homework.title
            ) {
                notifyText = '';
            }

            this.schoolClassService.notifyClass(
                homework.notifySchoolClass,
                homework.title,
                `${notifyText}https://${this.applicationStateService.platformDomain}/${homework.link}`
            );
        }

        this.modalService.openModal(instantiatedHomework);
        this.communicationService.announceSavedHomework(instantiatedHomework);
        this.communicationService.clearHomework();
    }

    get exercises(): Array<string> {
        return this.homeworkService.formAddedHomeworks.map(e => e.text);
    }

    onExerciseChange(event: { add: boolean; exercise: string }): void {
        if (event.add) {
            this.homeworkService.addExercise(event.exercise);
        } else {
            this.homeworkService.removeExercise(event.exercise);
        }
    }
}
